/**
 * A hash router, in twenty lines.
 *
 * The Front Door used to be one page with a rail that scrolled to anchors, and the
 * code said so: "none of these are places you could link someone to." That was true
 * of scroll positions. It stopped being true the moment the technical detail moved
 * to its own screens — the estate map *is* somewhere you would send someone, and a
 * page you cannot link to or reload back into is not a page.
 *
 * Hash rather than the History API, deliberately. Firebase Hosting rewrites `**` to
 * index.html so path routing would work, but the hash costs no server config, keeps
 * `steyn-frontdoor.web.app` and both custom domains behaving identically, and cannot
 * be broken by a future change to the rewrite rules. There is no SEO to lose behind
 * a sign-in gate.
 */

export type Route = "home" | "status" | "estate" | "tablet" | "handbook";

/**
 * Five routes, and two of them are not pages.
 *
 * `tablet` (F1) and `handbook` (F26) render outside `Shell` and outside the rail —
 * one is a wall object and one is a document. They are still routes rather than
 * separate builds because everything else about them is this app: the same tokens,
 * the same theme, the same registry, one deploy. What they do not share is the
 * chrome, and `App.svelte` is where that fork lives.
 *
 * `tablet` in particular is the only route in the estate that renders *before* the
 * sign-in gate, which is the whole feature: a kitchen screen that asks who you are
 * has already failed.
 */
const ROUTES: Route[] = ["home", "status", "estate", "tablet", "handbook"];

function fromHash(): Route {
  const raw = location.hash.replace(/^#\/?/, "").split("?")[0];
  // Unknown hashes fall back to home rather than rendering nothing. A typo in a
  // shared link should land somewhere useful, not on a blank screen.
  return (ROUTES as string[]).includes(raw) ? (raw as Route) : "home";
}

class Router {
  route = $state<Route>(fromHash());

  constructor() {
    // Covers the back button, a pasted link, and a reload — all three are the same
    // event, and handling only the click would break the first two.
    addEventListener("hashchange", () => {
      this.route = fromHash();
      // A route change is a new page as far as a reader is concerned, so it starts
      // at the top. Without this, navigating from the bottom of the estate table to
      // Home lands you halfway down the portal grid.
      scrollTo({ top: 0, behavior: "instant" });
    });
  }

  go(route: Route): void {
    // Assigning the hash is what fires hashchange, so `route` is updated there
    // rather than here — one path in, no chance of the two disagreeing.
    location.hash = route === "home" ? "#/" : `#/${route}`;
    if (fromHash() === this.route) this.route = route; // same-hash click still lands
  }
}

export const router = new Router();
